/* global Office */

import { WorkspaceData } from "../../types/workspace"

/**
 * Gets the current Office user's email address
 * @returns Promise that resolves to the user's email address or null if not available
 */
export const getCurrentUserEmail = (): Promise<string | null> => {
    return new Promise((resolve) => {
        try {
            // Check if Office is available
            if (typeof Office === 'undefined') {
                console.warn('Office object not available')
                resolve(null)
                return
            }

            // For Outlook add-ins, we can get the user's email from the mailbox
            if (Office?.context?.mailbox?.userProfile?.emailAddress) {
                resolve(Office.context.mailbox.userProfile.emailAddress)
                return
            }

            // Alternative method: Get from roamingSettings if available
            if (Office?.context?.roamingSettings) {
                const savedEmail = Office.context.roamingSettings.get('userEmail')
                if (savedEmail) {
                    resolve(savedEmail as string)
                    return
                }
            }

            // If no email found, resolve with null
            console.warn('No user email found in Office context')
            resolve(null)
        } catch (error) {
            console.error('Error getting user email:', error)
            resolve(null)
        }
    })
}

/**
 * Fetches workspace data from the API
 * @param apiUrl The base API URL
 * @param email The user's email address
 * @param apiKey The API key for authentication
 * @returns Promise that resolves to workspace data or null if failed
 */
export const fetchWorkspaceData = async (apiUrl: string, email: string, apiKey?: string): Promise<WorkspaceData> => {
    try {
        const url = `${apiUrl}/invite-addresses?email=${encodeURIComponent(email)}`

        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
        }

        if (apiKey && apiKey !== 'Not found' && apiKey.trim() !== '') {
            headers['X-API-Key'] = apiKey
        }

        const response = await fetch(url, {
            method: 'GET',
            headers,
        })

        if (!response.ok) {
            // Log more details about the error
            console.error(`API Error Details:`, {
                status: response.status,
                statusText: response.statusText,
                url: response.url
            })

            // Try to get error message from response body
            try {
                const errorText = await response.text()
                console.error('Error response body:', errorText)
            } catch (bodyError) {
                console.error('Could not read error response body:', bodyError)
            }

            throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        return data
    } catch (error) {
        console.error('Error fetching workspace data:', error)
        throw error
    }
}